import { FunctionComponent } from "react";
import { UseFormSetValue } from "react-hook-form";

import { useTranslation } from "react-i18next";

import { MultiAutoComplete } from "@/components/MultiAutoComplete/MultiAutoComplete";

import { Toggle } from "@/components/Toggle/Toggle";

import { SelectField } from "@/components/SelectField/SelectField";

import {
	CompanyEnrichAutoComplete,
	CompanyEnrichAutoCompleteItem,
} from "./CompanyEnrichAutoComplete";
import { CreateCompanyEnrich, Industry, MAX_COMPANY_DOMAINS } from "../schema/companyEnrich";
import { normalizeDomainInput } from "../utils/normalizeDomainInput";
import { EMPLOYEE_RANGES } from "../constants/employees.constants";
import { REVENUE_RANGES } from "../constants/revenue.constants";
import { COMPANY_TYPES } from "../constants/companyType.constants";

interface CompanyFiltersSectionProps {
	formValues: Partial<CreateCompanyEnrich>;
	setValue: UseFormSetValue<CreateCompanyEnrich>;
	industryOptions: { id: string; title: string }[];
	industriesData: Industry[];
	fetchTechnologies: (query: string) => Promise<CompanyEnrichAutoCompleteItem[]>;
	fetchCompanyDomains: (query: string) => Promise<CompanyEnrichAutoCompleteItem[]>;
	showExcludeIndustries: boolean;
	setShowExcludeIndustries: (v: boolean) => void;
	excludeDomains?: string[];
}

const CURRENT_YEAR = new Date().getFullYear();

const FOUNDING_YEAR_OPTIONS = [
	{ label: "Any", value: null },
	...Array.from({ length: CURRENT_YEAR - 1969 }, (_, i) => {
		const year = 1970 + i;

		return { label: String(year), value: year };
	}),
];

export const CompanyFiltersSection: FunctionComponent<CompanyFiltersSectionProps> = ({
	formValues,
	setValue,
	industryOptions,
	industriesData,
	fetchTechnologies,
	fetchCompanyDomains,
	showExcludeIndustries,
	setShowExcludeIndustries,
	excludeDomains = [],
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`companyEnrich.${key}`);

	const CATEGORY_OPTIONS = [
		{ id: "b2b", title: ts("categoryB2b") },
		{ id: "b2c", title: ts("categoryB2c") },
		{ id: "b2g", title: ts("categoryB2g") },
		{ id: "e-commerce", title: ts("categoryEcommerce") },
		{ id: "media", title: ts("categoryMedia") },
		{ id: "service-provider", title: ts("categoryServiceProvider") },
		{ id: "mobile", title: ts("categoryMobile") },
		{ id: "saas", title: ts("categorySaas") },
	];

	return (
		<>
			<div className="flex flex-col gap-4 w-full mb-4">
				<CompanyEnrichAutoComplete
					allowCustomValues
					normalizeCustomValue={normalizeDomainInput}
					fetchFn={fetchCompanyDomains}
					handleSelect={(value) => {
						setValue("domains", (value?.map((r) => r.id) || []).slice(0, MAX_COMPANY_DOMAINS), {
							shouldDirty: true,
							shouldValidate: true,
						});
					}}
					label={`${ts("domainsLabel")} (${formValues.domains?.length ?? 0}/${MAX_COMPANY_DOMAINS})`}
					disabledItems={excludeDomains}
					placeholder={ts("domainsPlaceholder")}
					selectedItems={
						formValues.domains?.map((r) => ({
							id: r,
							title: r,
						})) || []
					}
					testId="autocomplete-domains"
				/>
			</div>

			<div className="flex flex-row gap-4 w-full mb-4">
				<MultiAutoComplete
					stayOn
					data={industryOptions}
					handleSelect={(value) => {
						const selectedNames = value?.map((r) => String(r.id)) || [];
						const selected = industriesData.filter((ind) => selectedNames.includes(ind.name));

						setValue("companyFilterSearch.industries", selected, {
							shouldDirty: true,
							shouldValidate: true,
						});
					}}
					label={ts("industriesLabel")}
					placeholder={ts("industriesPlaceholder")}
					selectedItems={(formValues.companyFilterSearch?.industries || []).map((ind) => ({
						id: ind.name,
						title: ind.name,
					}))}
				/>
			</div>

			<div className="flex flex-col gap-4 w-full mb-4">
				<div className="flex flex-row gap-2 items-center">
					<Toggle
						isChecked={showExcludeIndustries}
						onChange={() => {
							const nextShown = !showExcludeIndustries;

							setShowExcludeIndustries(nextShown);

							// PREC-3868: only switching the filter OFF drops its values. Revealing an
							// already-configured exclusion must never silently change the search.
							if (!nextShown) {
								setValue("companyFilterSearch.exclude.industries", [], {
									shouldDirty: true,
									shouldValidate: true,
								});
							}
						}}
					/>
					<div className="text-sm font-medium">{ts("excludeIndustriesLabel")}</div>{" "}
				</div>
				{showExcludeIndustries && (
					<MultiAutoComplete
						stayOn
						data={industryOptions}
						handleSelect={(value) => {
							const selectedNames = value?.map((r) => String(r.id)) || [];
							const selected = industriesData.filter((ind) => selectedNames.includes(ind.name));

							setValue("companyFilterSearch.exclude.industries", selected, {
								shouldDirty: true,
								shouldValidate: true,
							});
						}}
						label={ts("excludeIndustriesLabel")}
						placeholder={ts("excludeIndustriesPlaceholder")}
						selectedItems={(formValues.companyFilterSearch?.exclude?.industries || []).map(
							(ind) => ({
								id: ind.name,
								title: ind.name,
							})
						)}
					/>
				)}
			</div>

			<div className="flex flex-row items-end gap-4 w-full mb-4">
				<SelectField
					label={ts("foundedYearLabel")}
					name="foundedYearMin"
					selectedOption={formValues.companyFilterSearch?.foundedYear?.min ?? null}
					handleSelect={(val) => {
						setValue(
							"companyFilterSearch.foundedYear",
							{ ...formValues.companyFilterSearch?.foundedYear, min: val as number | null },
							{ shouldDirty: true, shouldValidate: true }
						);
					}}
					options={FOUNDING_YEAR_OPTIONS.map(({ label, value }) => ({
						title: label,
						value,
					}))}
					hideEmptyValue
				/>

				<span className="-translate-y-1/2 text-[14px]">{ts("to")}</span>

				<SelectField
					name="foundedYearMax"
					selectedOption={formValues.companyFilterSearch?.foundedYear?.max ?? null}
					handleSelect={(val) => {
						setValue(
							"companyFilterSearch.foundedYear",
							{ ...formValues.companyFilterSearch?.foundedYear, max: val as number | null },
							{ shouldDirty: true, shouldValidate: true }
						);
					}}
					options={FOUNDING_YEAR_OPTIONS.map(({ label, value }) => ({
						title: label,
						value,
					}))}
					hideEmptyValue
				/>
			</div>

			<div className="flex flex-row gap-4 w-full mb-4">
				<MultiAutoComplete
					stayOn
					data={EMPLOYEE_RANGES}
					handleSelect={(value) => {
						setValue("companyFilterSearch.employees", value?.map((r) => r.id.toString()) || [], {
							shouldDirty: true,
							shouldValidate: true,
						});
					}}
					label={ts("employeesLabel")}
					placeholder={ts("employeesPlaceholder")}
					selectedItems={
						formValues.companyFilterSearch?.employees?.map((r) => ({
							id: r,
							title: EMPLOYEE_RANGES.find((e) => e.id === r)?.title || r,
						})) || []
					}
				/>
			</div>
			<div className="flex flex-row gap-4 w-full mb-4">
				<MultiAutoComplete
					data={REVENUE_RANGES}
					handleSelect={(value) => {
						setValue("companyFilterSearch.revenue", value?.map((r) => r.id.toString()) || [], {
							shouldDirty: true,
							shouldValidate: true,
						});
					}}
					stayOn
					label={ts("revenueLabel")}
					placeholder={ts("revenuePlaceholder")}
					selectedItems={
						formValues.companyFilterSearch?.revenue?.map((r) => ({
							id: r,
							title: REVENUE_RANGES.find((e) => e.id === r)?.title || r,
						})) || []
					}
				/>
			</div>

			<div className="flex flex-col gap-4 w-full mb-4">
				<CompanyEnrichAutoComplete
					fetchFn={fetchTechnologies}
					handleSelect={(value) => {
						setValue("companyFilterSearch.technologies", value?.map((r) => r.id) || [], {
							shouldDirty: true,
							shouldValidate: true,
						});
					}}
					label={ts("technologiesLabel")}
					placeholder={ts("technologiesPlaceholder")}
					selectedItems={
						formValues.companyFilterSearch?.technologies?.map((r) => ({
							id: r,
							title: r,
						})) || []
					}
					testId="autocomplete-technologies"
				/>
			</div>

			<div className="flex flex-row gap-4 w-full mb-4">
				<MultiAutoComplete
					data={COMPANY_TYPES}
					stayOn
					handleSelect={(value) => {
						setValue("companyFilterSearch.type", value?.map((r) => r.id.toString()) || [], {
							shouldDirty: true,
							shouldValidate: true,
						});
					}}
					label={ts("companyTypeLabel")}
					placeholder={ts("companyTypePlaceholder")}
					selectedItems={
						formValues.companyFilterSearch?.type?.map((r) => ({
							id: r,
							title: COMPANY_TYPES.find((e) => e.id === r)?.title || r,
						})) || []
					}
				/>
			</div>
			<div className="flex flex-row gap-4 w-full mb-4">
				<MultiAutoComplete
					data={CATEGORY_OPTIONS}
					handleSelect={(value) => {
						setValue("companyFilterSearch.category", value?.map((r) => r.id.toString()) || [], {
							shouldDirty: true,
							shouldValidate: true,
						});
					}}
					stayOn
					label={ts("companyCategoryLabel")}
					placeholder={ts("companyCategoryPlaceholder")}
					selectedItems={
						formValues.companyFilterSearch?.category?.map((r) => ({
							id: r,
							title: CATEGORY_OPTIONS.find((e) => e.id === r)?.title || r,
						})) || []
					}
				/>
			</div>
		</>
	);
};
